import axios, { AxiosHeaders, type AxiosInstance } from 'axios';
import { clearOAuthSession, getAccessToken } from './auth/oauth';
import { adminConfig } from './config/env';

export const OAUTH_EXPIRED_EVENT = 'kkutu-admin-oauth-expired';

interface AdminApiOptions {
  baseURL?: string;
  tokenProvider?: () => string | null;
  onUnauthorized?: () => void;
}

export function createAdminApi({
  baseURL = `${adminConfig.apiOrigin}/api/admin`,
  tokenProvider = getAccessToken,
  onUnauthorized = () => {
    clearOAuthSession();
    window.dispatchEvent(new Event(OAUTH_EXPIRED_EVENT));
  },
}: AdminApiOptions = {}): AxiosInstance {
  const client = axios.create({
    baseURL,
    withCredentials: false,
    headers: { 'X-Requested-With': 'XMLHttpRequest' },
  });
  let lastExpiredToken: string | null | undefined;

  client.interceptors.request.use(config => {
    const token = tokenProvider();
    config.headers = AxiosHeaders.from(config.headers);
    const authorization = token ? `Bearer ${token}` : null;
    if (authorization) config.headers.set('Authorization', authorization);
    if (authorization !== lastExpiredToken) lastExpiredToken = undefined;
    return config;
  });

  client.interceptors.response.use(response => response, error => {
    if (error.response?.status === 401) {
      const authorization = AxiosHeaders.from(error.config?.headers).get('Authorization');
      const rejectedToken = typeof authorization === 'string' ? authorization : null;
      const activeToken = tokenProvider();
      const activeAuthorization = activeToken ? `Bearer ${activeToken}` : null;
      if (rejectedToken === activeAuthorization && rejectedToken !== lastExpiredToken) {
        lastExpiredToken = rejectedToken;
        onUnauthorized();
      }
    }
    return Promise.reject(error);
  });

  return client;
}

export const KKuTuIOApi = createAdminApi();
